/**
 * Owned Media — tabla genérica de una entidad hija dentro de una pestaña de la
 * cuenta (§6.1, §8.3): columnas resumen del schema, badge de estado, acciones
 * según el permiso efectivo (editar, duplicar, archivar/restaurar, marcar
 * inactivo, baja/renovación de acceso, cerrar asignación, eliminar link) y un
 * panel lateral con la ficha completa + historial del registro.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, ArchiveRestore, Copy, KeyRound, MoreHorizontal, Pencil, Plus, Trash2, UserMinus, AlertTriangle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { usePagination } from '@/hooks/usePagination';
import { useOmlMutations, type OmlAccountDetail, type OmlConfig } from '@/hooks/useOml';
import { isRiskyAccess, accessExpiringSoon, type Values } from '../../../../shared/oml-rules';
import { OML_SCHEMA, type OmlChildType, type OmlFieldDef } from '../../../../shared/types/oml';
import { EntityForm } from './EntityForm';
import { FieldValue } from './FieldValue';
import { HistoryPanel } from './HistoryPanel';
import { ExportMenu } from './ExportMenu';
import { SUMMARY_FIELDS, entityLabel, entityName, fieldLabel } from './oml-ui';

type Row = Values & { id: string; locked: string[]; archived?: boolean };

export function EntityTable({ type, detail, config, members, rows, showArchived = false, extraColumns }: {
  type: OmlChildType;
  detail: OmlAccountDetail;
  config: OmlConfig | undefined;
  members: { email: string; name: string; role?: string; area?: string; team?: string; status?: string }[];
  rows: Row[];
  showArchived?: boolean;
  extraColumns?: string[];
}) {
  const { t } = useTranslation();
  const m = useOmlMutations();
  const def = OML_SCHEMA[type];
  const acc = detail.access;
  const accountId = detail.account.id;
  const perms = acc.entities[type] ?? {};
  const [editing, setEditing] = useState<Row | null | 'new'>(null);
  const [viewing, setViewing] = useState<Row | null>(null);
  const [ask, setAsk] = useState<{ kind: 'revoke' | 'renew' | 'close' | 'delete'; row: Row } | null>(null);
  const [askText, setAskText] = useState('');

  const names = useMemo(() => Object.fromEntries(members.map((x) => [x.email, x.name])), [members]);
  const relationNames = useMemo(() => {
    const out: Record<string, string> = {};
    for (const k of Object.keys(detail.children) as OmlChildType[]) for (const e of detail.children[k]) out[e.id] = entityName(k, e as Values);
    return out;
  }, [detail.children]);
  const cols = (extraColumns ?? SUMMARY_FIELDS[type]).map((k) => def.fields.find((f) => f.key === k)).filter((f): f is OmlFieldDef => !!f);
  const activeFlag = def.fields.find((f) => f.isActiveFlag)?.key;
  const list = useMemo(() => rows.filter((r) => showArchived || !r.archived), [rows, showArchived]);
  // Contador y exportación siguen sobre `list` completa; solo se pagina lo que se monta.
  const { pageItems, footer: paginationFooter } = usePagination(list, 25);
  const ctx = (r: Row) => ({ accountId, names, relationNames, locked: new Set(r.locked), type });

  const runAsk = async () => {
    if (!ask) return;
    const { kind, row } = ask;
    if (kind === 'revoke') await m.revokeAccess.mutateAsync({ accountId, id: row.id, justification: askText || undefined });
    if (kind === 'renew') await m.renewAccess.mutateAsync({ accountId, id: row.id, expiresAt: askText });
    if (kind === 'close') await m.closeAssignment.mutateAsync({ accountId, id: row.id, reason: askText || undefined });
    if (kind === 'delete') await m.deleteLink.mutateAsync({ accountId, id: row.id, justification: askText || undefined });
    setAsk(null); setAskText('');
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t('oml.table.count', '{{n}} registro(s)', { n: list.length })}</p>
        <div className="flex items-center gap-2">
          <ExportMenu canExport={acc.canExport} scope="entities" accountId={accountId} entityType={type} entityIds={list.map((r) => r.id)} />
          {perms.create && <Button size="sm" onClick={() => setEditing('new')}><Plus className="h-4 w-4 mr-1" />{t('oml.table.new', 'Nuevo')}</Button>}
        </div>
      </div>

      {list.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          {t('oml.table.empty', 'Todavía no hay {{entity}} en esta cuenta.', { entity: entityLabel(t, type, true).toLowerCase() })}
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{fieldLabel(t, type, def.nameField)}</TableHead>
                {cols.map((f) => <TableHead key={f.key}>{fieldLabel(t, type, f.key)}</TableHead>)}
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((r) => {
                const risky = type === 'platform' && isRiskyAccess(r.accessType);
                const expiring = type === 'platform' && !r.revokedAt && accessExpiringSoon(r.expiresAt, detail.today);
                return (
                  <TableRow key={r.id} className={cn('cursor-pointer', r.archived && 'opacity-60', (r.revokedAt || r[activeFlag ?? ''] === false) && 'opacity-60')} onClick={() => setViewing(r)}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {risky && <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0" aria-label={t('oml.platform.risky', 'Acceso por cuenta compartida o personal')} />}
                        {expiring && <KeyRound className="h-4 w-4 text-amber-600 shrink-0" aria-label={t('oml.platform.expiring', 'Vence en menos de 30 días')} />}
                        <span>{entityName(type, r)}</span>
                        {r.archived && <span className="text-xs text-muted-foreground">({t('oml.table.archived', 'archivado')})</span>}
                        {!!r.revokedAt && <span className="text-xs text-muted-foreground">({t('oml.platform.revoked', 'baja')})</span>}
                        {type === 'assignment' && !!r.to && String(r.to) < detail.today && <span className="text-xs text-muted-foreground">({t('oml.assignment.closed', 'cerrada')})</span>}
                      </div>
                    </TableCell>
                    {cols.map((f) => <TableCell key={f.key} className="max-w-[260px]"><FieldValue def={f} value={r[f.key]} ctx={ctx(r)} compact /></TableCell>)}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-0.5">
                      {perms.edit && !r.archived && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" aria-label={t('common.edit', 'Editar')} onClick={() => setEditing(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7" aria-label={t('oml.table.actions', 'Acciones')}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setViewing(r)}>{t('oml.table.view', 'Ver ficha e historial')}</DropdownMenuItem>
                          {perms.edit && !r.archived && <DropdownMenuItem onClick={() => setEditing(r)}><Pencil className="h-4 w-4 mr-2" />{t('common.edit', 'Editar')}</DropdownMenuItem>}
                          {perms.duplicate && !r.archived && <DropdownMenuItem onClick={() => m.duplicateEntity.mutate({ accountId, type, id: r.id })}><Copy className="h-4 w-4 mr-2" />{t('oml.table.duplicate', 'Duplicar')}</DropdownMenuItem>}
                          {activeFlag && perms.edit && !r.archived && (
                            <DropdownMenuItem onClick={() => m.updateEntity.mutate({ accountId, type, id: r.id, patch: { [activeFlag]: r[activeFlag] === false }, touched: [activeFlag] })}>
                              {r[activeFlag] === false ? t('oml.table.mark_active', 'Marcar como activo') : t('oml.table.mark_inactive', 'Marcar como inactivo')}
                            </DropdownMenuItem>
                          )}
                          {type === 'platform' && perms.edit && !r.revokedAt && <DropdownMenuItem onClick={() => setAsk({ kind: 'revoke', row: r })}><KeyRound className="h-4 w-4 mr-2" />{t('oml.platform.revoke', 'Registrar baja de acceso')}</DropdownMenuItem>}
                          {type === 'platform' && perms.edit && <DropdownMenuItem onClick={() => { setAsk({ kind: 'renew', row: r }); setAskText(String(r.expiresAt ?? '').slice(0, 10)); }}>{t('oml.platform.renew', 'Renovar vencimiento')}</DropdownMenuItem>}
                          {type === 'assignment' && perms.edit && !r.to && <DropdownMenuItem onClick={() => setAsk({ kind: 'close', row: r })}><UserMinus className="h-4 w-4 mr-2" />{t('oml.assignment.close', 'Cerrar asignación')}</DropdownMenuItem>}
                          {(perms.archive || perms.delete) && <DropdownMenuSeparator />}
                          {perms.archive && (r.archived
                            ? <DropdownMenuItem onClick={() => m.archiveEntity.mutate({ accountId, type, id: r.id, archived: false })}><ArchiveRestore className="h-4 w-4 mr-2" />{t('oml.table.restore', 'Restaurar')}</DropdownMenuItem>
                            : <DropdownMenuItem onClick={() => m.archiveEntity.mutate({ accountId, type, id: r.id, archived: true })}><Archive className="h-4 w-4 mr-2" />{t('oml.table.archive', 'Archivar')}</DropdownMenuItem>)}
                          {perms.delete && type === 'link' && <DropdownMenuItem className="text-destructive" onClick={() => setAsk({ kind: 'delete', row: r })}><Trash2 className="h-4 w-4 mr-2" />{t('oml.link.delete', 'Eliminar (cargado por error)')}</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="px-3 pb-3">{paginationFooter}</div>
        </div>
      )}

      {editing !== null && (
        <EntityForm open onOpenChange={(o) => { if (!o) setEditing(null); }} type={type} accountId={accountId} entity={editing === 'new' ? undefined : editing} detail={detail} config={config} members={members} />
      )}

      <Sheet open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {viewing && (
            <>
              <SheetHeader>
                <SheetTitle>{entityName(type, viewing)}</SheetTitle>
                <SheetDescription>{entityLabel(t, type)} · {detail.account.name}</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-3">
                {def.fields.filter((f) => f.key !== def.nameField && !(f.system && (viewing[f.key] === undefined || viewing[f.key] === null))).map((f) => (
                  <div key={f.key} className="grid grid-cols-[minmax(120px,40%)_1fr] gap-2 text-sm border-b pb-2 last:border-0">
                    <div className="text-muted-foreground flex items-center gap-1">{fieldLabel(t, type, f.key)}{f.restricted && <Lock className="h-3 w-3" />}</div>
                    <div className="min-w-0"><FieldValue def={f} value={viewing[f.key]} ctx={ctx(viewing)} /></div>
                  </div>
                ))}
                <div className="flex gap-2 pt-2">
                  {perms.edit && !viewing.archived && <Button size="sm" onClick={() => { setEditing(viewing); setViewing(null); }}><Pencil className="h-4 w-4 mr-1" />{t('common.edit', 'Editar')}</Button>}
                </div>
                {acc.canSeeHistory !== false && (<>
                  <h4 className="text-sm font-semibold pt-4">{t('oml.history.title', 'Historial de cambios')}</h4>
                  <HistoryPanel accountId={accountId} entityId={viewing.id} names={names} limit={50} />
                </>)}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={!!ask} onOpenChange={(o) => { if (!o) { setAsk(null); setAskText(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {ask?.kind === 'revoke' && t('oml.platform.revoke', 'Registrar baja de acceso')}
              {ask?.kind === 'renew' && t('oml.platform.renew', 'Renovar vencimiento')}
              {ask?.kind === 'close' && t('oml.assignment.close', 'Cerrar asignación')}
              {ask?.kind === 'delete' && t('oml.link.delete', 'Eliminar (cargado por error)')}
            </DialogTitle>
            <DialogDescription>{ask ? entityName(type, ask.row) : ''}</DialogDescription>
          </DialogHeader>
          {ask?.kind === 'renew' ? (
            <div className="space-y-1.5"><Label>{t('oml.platform.new_expiry', 'Nueva fecha de vencimiento')}</Label><Input type="date" value={askText} onChange={(e) => setAskText(e.target.value)} /></div>
          ) : (
            <div className="space-y-1.5"><Label>{t('oml.form.reason_optional', 'Motivo (opcional, queda en el historial)')}</Label><Textarea value={askText} onChange={(e) => setAskText(e.target.value)} rows={2} /></div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAsk(null)}>{t('common.cancel', 'Cancelar')}</Button>
            <Button variant={ask?.kind === 'delete' ? 'destructive' : 'default'} onClick={runAsk} disabled={(ask?.kind === 'renew' && !askText) || m.revokeAccess.isPending || m.renewAccess.isPending || m.closeAssignment.isPending || m.deleteLink.isPending}>{t('common.confirm', 'Confirmar')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
